import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { Keyboard } from "react-native";
import { createParam } from "solito";
import { useRouter } from "solito/router";
import type { z } from "zod";
import { Lock as LockIcon } from "@tamagui/lucide-icons";
import type { AuthenticateResultErrorType } from "@medbillai/generated-graphql-types";
import { applyFormErrors, FullscreenSpinner, isWeb, OAuthButtons, OAuthDivider, type OAuthProviderType, Paragraph2, ParagraphSmall, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { type LoginParams as UrlLoginParams, verifyEmailUrl } from "@medbillai/utils/urls";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { analyticsService } from "../../lib/analytics";
import { useAuth } from "../../lib/auth";
import { useBoolVariation } from "../../lib/flag";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { SingularEvents, singularService } from "../../lib/singular";
import { useAlerts } from "../../provider/alerts";
import { BotControl } from "./components/BotControl";
import { useBotControl } from "./components/BotControlProvider";
import { SignInForm, type SignInSchema } from "./components/SignInForm";
export type LoginParams = Pick<UrlLoginParams, "forceLogout" | "authError" | "variant">;
const {
  useParams
} = createParam<LoginParams>();
const errorMessages: Record<AuthenticateResultErrorType, string> = {
  INVALID_PKCE_CODE_CHALLENGE: "The login link you followed was requested from a different device. For your security, please request log in again from this device.",
  UNABLE_TO_AUTH_MAGIC_LINK: "The login link you followed has expired or has already been used. Please try logging in again.",
  INVALID_AUTH_OAUTH: "Your auth provider is not working, please try again with your email.",
  UNABLE_TO_AUTH_OAUTH: "Your auth provider is not working, please try again with your email.",
  INVALID_EMAIL_OTP: "Your verification code has expired or has already been used. Please try logging in again.",
  UNKNOWN_ERROR: "Something went wrong, please try again"
};
type LoginScreenProps = {
  /** Partner invite token; threaded into the auth mutations to bind at signup. */
  inviteToken?: string;
};
export const LoginScreen = ({
  inviteToken
}: LoginScreenProps) => {
  const {
    params
  } = useParams();
  const {
    push,
    replace
  } = useRouter();
  const {
    showAlert
  } = useAlerts();
  const {
    isReady,
    isAuthenticated,
    sendMagicLink,
    logout,
    user,
    startOAuth,
    getAndClearIntendedPath
  } = useAuth();
  const isSignUpOnboarding = useSignUpOnboardingStore(state => state.isSignUpOnboarding);
  const [forceEnable, setForceEnable] = useState(false);
  const {
    getBotControlToken,
    resetBotControl,
    isBotControlEnabled
  } = useBotControl();
  const isOAuthEnabled = useBoolVariation(Flag.OAUTH_ENABLED, false);
  const errorMessage = params?.authError ? errorMessages[params.authError] ?? errorMessages.UNKNOWN_ERROR : undefined;
  const form = useForm<z.infer<typeof SignInSchema>>();
  const {
    isSubmitting,
    isSubmitted,
    isValid
  } = useFormState({
    control: form.control
  });
  // `isSubmitting` always disables — `forceEnable` only relaxes the
  // form-validity guard so a prefilled web form can submit before the user
  // touches it. Without this, an in-flight `sendMagicLink` could fire twice.
  const isSubmitDisabled = isSubmitting || (forceEnable ? false : isSubmitted && !isValid);
  const loggedOut = useRef(false);
  useEffect(() => {
    if (params?.forceLogout && !loggedOut.current) {
      loggedOut.current = true;
      void logout();
    }
  }, [params?.forceLogout, logout]);
  useEffect(() => {
    if (user?.profile?.email) {
      // Should dirty does not work on web.. so we need to force the form to be
      // enabled.
      form.setValue("email", user.profile.email, {
        shouldDirty: true
      });
      if (isWeb) {
        setForceEnable(true);
      }
    } else if (!isAuthenticated || params?.forceLogout) {
      form.reset();
      setForceEnable(false);
    }
  }, [form, user?.profile?.email, isAuthenticated, params?.forceLogout]);
  const doSendMagicLink = useCallback(async ({
    email
  }: z.infer<typeof SignInSchema>) => {
    try {
      const botControlToken = getBotControlToken();
      // If bot control is enabled, a valid token is required.
      if (isBotControlEnabled && !botControlToken) {
        analyticsService.trackEvent("LoginEmailSendFailed", {
          reason: "BOT_CONTROL"
        });
        showAlert({
          type: "error",
          message: "Please verify you are human."
        });
        return;
      }
      const userSegmentationData = useSignUpOnboardingStore.getState().userSegmentationData;
      const result = await sendMagicLink({
        email: email,
        botControlToken: botControlToken,
        userSegmentationData,
        inviteToken
      });
      if (applyFormErrors(form, result)) {
        analyticsService.trackEvent("LoginEmailSendFailed", {
          reason: "FORM_ERROR"
        });
        if (isBotControlEnabled) {
          resetBotControl();
        }
        return;
      }
      // Sent email successfully
      Keyboard.dismiss();
      if (result.__typename === "SendMagicLinkResponse") {
        singularService.setUser({
          id: result.user.id,
          email
        });
        if (result.created) {
          analyticsService.trackEvent("UserCreated", {
            email
          });
        }
      }
      analyticsService.trackEvent("SubmitLogin", {
        method: "EMAIL",
        deliveryMethod: result.deliveryMethod
      });
      singularService.event(SingularEvents.startedRegistration, {
        type: "email"
      });
      push(verifyEmailUrl(email));
    } catch (error) {
      console.error("Error while sending magic link", error);
      analyticsService.trackEvent("LoginEmailSendFailed", {
        reason: "EXCEPTION"
      });
      showAlert({
        type: "error",
        message: "Unable to send a login email, please try again."
      });

      // If bot control is enabled, reset the token and component
      if (isBotControlEnabled) {
        resetBotControl();
      }
    }
  }, [sendMagicLink, form, showAlert, push, isBotControlEnabled, getBotControlToken, resetBotControl, inviteToken]);
  const handleOAuthPress = useCallback(async (provider: OAuthProviderType) => {
    try {
      singularService.event(SingularEvents.startedRegistration, {
        type: "oauth"
      });
      await startOAuth(provider, inviteToken);
    } catch (error) {
      console.error("Error during OAuth authentication", error);
      showAlert({
        type: "error",
        message: "Unable to authenticate with OAuth, please try again."
      });
    }
  }, [startOAuth, showAlert, inviteToken]);
  useEffect(() => {
    // If the user is already logged in and they are not force logging out,
    // redirect to the home page.
    if (isAuthenticated && !params?.forceLogout && isReady) {
      // Check for stored intended path first, fallback to home
      const intendedPath = getAndClearIntendedPath();
      replace(intendedPath || "/");
    }
  }, [isAuthenticated, params?.forceLogout, replace, isReady, getAndClearIntendedPath]);
  if (!isReady || isAuthenticated && !params?.forceLogout && isReady) {
    return <FullscreenSpinner />;
  }

  // Any `variant` means the link came from a signup hand-off; retired values
  // (the v2 flow's intents) still arrive from links already in the wild, and
  // must not fall through to the returning-user framing.
  // Invite activation and the anonToken redirect set the store without the param.
  const isSignUp = params?.variant !== undefined || isSignUpOnboarding;
  const title = isSignUp ? "Create your account" : "Welcome back";
  return <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-component="LoginScreen" data-sentry-source-file="login.tsx">
      <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-source-file="login.tsx">
        <OnboardingScreen.Nav showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="login.tsx" />
        <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="login.tsx">
          <OnboardingScreen.Header alignment="center" title={title} showLogo testID="login.welcome" data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="login.tsx" />
          {errorMessage ? <Paragraph2 color="$red10" textAlign="center" paddingBottom="$sp3">
              {errorMessage}
            </Paragraph2> : null}
          {isOAuthEnabled ? <YStack paddingBottom="$sp3" gap="$sp3">
              <OAuthButtons onPress={handleOAuthPress} />
              <OAuthDivider />
            </YStack> : null}
          <SignInForm form={form} onSubmit={doSendMagicLink} data-sentry-element="SignInForm" data-sentry-source-file="login.tsx" />
          <BotControl interactiveMargin="$3" data-sentry-element="BotControl" data-sentry-source-file="login.tsx" />
          <OnboardingScreen.Disclaimer data-sentry-element="OnboardingScreen.Disclaimer" data-sentry-source-file="login.tsx">
            <YStack alignItems="center" gap="$sp1" $platform-web={{
            paddingTop: "$sp3"
          }} data-sentry-element="YStack" data-sentry-source-file="login.tsx">
              <LockIcon color="$iconSubtle" size={12} strokeWidth={2.5} data-sentry-element="LockIcon" data-sentry-source-file="login.tsx" />
              <ParagraphSmall theme="alt1" maxWidth={260} textAlign="center" data-sentry-element="ParagraphSmall" data-sentry-source-file="login.tsx">
                Granted keeps your data private, secure, and fully encrypted.
              </ParagraphSmall>
            </YStack>
          </OnboardingScreen.Disclaimer>
        </OnboardingScreen.Content>
        <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="login.tsx">
          <OnboardingScreen.PrimaryAction variant="brand" onPress={form.handleSubmit(doSendMagicLink)} loading={isSubmitting} disabled={isSubmitDisabled} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="login.tsx">
            Continue
          </OnboardingScreen.PrimaryAction>
        </OnboardingScreen.Footer>
      </OnboardingScreen>
    </FormProvider>;
};